import { Input, Tooltip, Typography } from "antd-v5";
import useCorrelatedAssetColumns from "hooks/useCorrelatedAssetColumns";
import useDebounceCallback from "hooks/useDebounceCallback";
import useTableCustomizationHelper from "hooks/useTableCustomizationHelper";
import React, { useCallback, useMemo, useState } from "react";
import { AssetEsDto } from "types/assetdetails";

import ACButtonGroup, { ACButtonGroupItem } from "ac-components/ACButtonGroup";
import ACPaginationBar from "ac-components/ACPaginationBar/ACPaginationBar";
import ACTable from "ac-components/ACTable/ACTable";
import ACColumnDragWrapper from "ac-components/ac-column-drag-wrapper/ACColumnDragWrapper";
import ACColumnVisibilityDropdown from "ac-components/ac-column-visibility-dropdown/ACColumnVisibilityDropdown";
import ColumnsIcon from "ac-components/ac-icons/ColumnsIcon";
import SearchIcon from "ac-components/ac-icons/SearchIcon";
import ACResizableColumnCell from "ac-components/ac-resizable-column-cell/ACResizableColumnCell";
import ACSettingSectionCard from "ac-components/ac-setting-section-card/ACSettingSectionCard";
import ACSettingSectionTitle from "ac-components/ac-setting-section-title/ACSettingSectionTitle";

import CorrelatedAssetDetailPanel from "./CorrelatedAssetDetailPanel";
import { getCorrelationRuleTags } from "./correlationUtils";

const PAGE_SIZE = 5;
const PREF_KEY = "correlated_assets_table";

// Helper to safely search in fields that can be string or array
const safeSearchInField = (
  field: string[] | string | undefined | null,
  term: string,
): boolean => {
  if (!field) return false;
  if (Array.isArray(field)) {
    return field.some((item) => item?.toLowerCase().includes(term));
  }
  return field.toLowerCase().includes(term);
};

const CorrelatedAssetsSection = ({
  assetDetails,
}: {
  assetDetails: AssetEsDto;
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [selectedAsset, setSelectedAsset] = useState<AssetEsDto | null>(null);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);

  const { columns: allColumns, defaultHiddenKeys } = useCorrelatedAssetColumns({
    sourceMap: assetDetails.sourceMap ?? [],
  });

  const tableCustomizationHelper = useTableCustomizationHelper({
    columns: allColumns,
    draggable: true,
    alwaysVisibleKeys: [],
    defaultHiddenKeys,
    prefKey: PREF_KEY,
  });

  const originalLength = assetDetails.sourceMap?.length || 1;

  // sourceMap is already an array of AssetEsDto - no transformation needed
  const correlatedAssets = useMemo(() => {
    return assetDetails.sourceMap ?? [];
  }, [assetDetails.sourceMap]);

  // Filter by search term
  const filteredAssets = useMemo(() => {
    if (!searchTerm) return correlatedAssets;
    const term = searchTerm.toLowerCase();
    return correlatedAssets.filter((asset) => {
      const tags = getCorrelationRuleTags(
        asset.assetCorrelationContext?.rule ?? null,
      );
      const tagsMatch = tags?.some((tag) =>
        tag.label.toLowerCase().includes(term),
      );
      return (
        asset.name?.toLowerCase().includes(term) ||
        safeSearchInField(asset.hostname, term) ||
        asset.armorCodeType?.toLowerCase().includes(term) ||
        tagsMatch ||
        safeSearchInField(asset.source, term) ||
        safeSearchInField(asset.location, term)
      );
    });
  }, [correlatedAssets, searchTerm]);

  // Paginate
  const paginatedAssets = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredAssets.slice(start, start + PAGE_SIZE);
  }, [filteredAssets, page]);

  const onSearch = useDebounceCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      setPage(1);
    },
    [],
    1000,
  );

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowClick = useCallback((asset: AssetEsDto) => {
    setSelectedAsset(asset);
    setSidePanelOpen(true);
  }, []);

  const handleCloseSidePanel = useCallback(() => {
    setSidePanelOpen(false);
    setSelectedAsset(null);
  }, []);

  return (
    <div>
      <ACSettingSectionTitle
        title="Correlated Assets"
        description={`${originalLength - 1} assets correlated with this asset`}
      />
      <ACSettingSectionCard contentPadding={false}>
        <div
          className="d-flex justify-content-between align-items-center"
          style={{ padding: "16px 24px" }}
        >
          <Input
            prefix={<SearchIcon />}
            placeholder="Search correlated assets"
            style={{ width: 256 }}
            onChange={onSearch}
            allowClear
          />
          <ACButtonGroup size="md">
            <ACColumnVisibilityDropdown
              columns={allColumns}
              columnVisibilityConfig={
                tableCustomizationHelper.columnVisibilityConfig
              }
              onChange={tableCustomizationHelper.onColumnVisibilityChange}
              onColumnOrderChange={tableCustomizationHelper.onColumnOrderChange}
              onResetColumnOptions={
                tableCustomizationHelper.onResetColumnOptions
              }
              activeColumns={tableCustomizationHelper.exportColumns}
              alwaysVisibleKeys={tableCustomizationHelper.alwaysVisibleKeys}
              fixedColumns={0}
            >
              <Tooltip title="Manage Columns">
                <ACButtonGroupItem id="correlated-assets-manage-columns">
                  <ColumnsIcon />
                </ACButtonGroupItem>
              </Tooltip>
            </ACColumnVisibilityDropdown>
          </ACButtonGroup>
        </div>
        <ACColumnDragWrapper
          onDragEnd={tableCustomizationHelper.onColumnOrderChange}
        >
          <ACTable<AssetEsDto>
            columns={tableCustomizationHelper.columns}
            dataSource={paginatedAssets}
            customizationHelpers={tableCustomizationHelper}
            autoEnableScroll
            computeTableWidth
            components={{
              header: {
                cell: ACResizableColumnCell,
              },
            }}
            pagination={false}
            rowKey="id"
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: "pointer" },
            })}
          />
        </ACColumnDragWrapper>
        {filteredAssets.length > 0 && (
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ padding: "12px 24px" }}
          >
            <Typography.Text type="secondary">
              Showing {(page - 1) * PAGE_SIZE + 1} to{" "}
              {Math.min(page * PAGE_SIZE, filteredAssets.length)} of{" "}
              {filteredAssets.length} results
            </Typography.Text>
            {filteredAssets.length > PAGE_SIZE && (
              <ACPaginationBar
                total={filteredAssets.length}
                page={page}
                onChange={onPageChange}
                pageSize={PAGE_SIZE}
                showSizeChanger={false}
                showEdges={false}
                bordered={false}
              />
            )}
          </div>
        )}
      </ACSettingSectionCard>
      <CorrelatedAssetDetailPanel
        asset={selectedAsset}
        open={sidePanelOpen}
        onClose={handleCloseSidePanel}
      />
    </div>
  );
};

export default CorrelatedAssetsSection;
